import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {Puzzle, PuzzleConstants} from './puzzle.js';
import {BlobPrefabs} from './blob-prefabs.js';
import {RNG} from '../utils/rng.js';
import {BoardColor, BoardObject} from './board-object.js';
import {WLUtils} from '../utils/WLUtils.js';
import {vec3} from 'gl-matrix';
import {LevelDependentPieces} from '../classes/LevelDependentPieces.js';
import {PieceSpawnRules} from '../classes/PieceSpawnRules.js';
import {GameModeType} from '../enums/GameModeType.js';
import {GameModePieceSpawnRules} from '../classes/GameModePieceSpawnRules.js';
import {PuzzleUtils} from '../classes/PuzzleUtils.js';
import {LevelSelect} from './level-select.js';

// Note: the original LineSpawnerNormal.cs class inherits from LineSpawner.
// But since there is only 1 inheriting class, and WLE can't easily get a base component with
// getComponent, I (TK) decided to make LineSpawnerNormal a standalone class and merge the code from the
// base class into this..

const tempVec3 = vec3.create();

export class LineSpawnerNormal extends Component {
    static TypeName = 'line-spawner-normal';
    static InheritProperties = true;

    @property.object({required: true})
    blobPrefabsObject!: Object3D;

    @property.enum(Object.keys(GameModeType).filter((e) => isNaN(Number(e))))
    gameMode: GameModeType;

    pieces: Map<string, BoardColor> = new Map();

    //TK: This property does not seem to be used in the original. I kept it in. Just
    // in case it turns out to be, or maybe we can start using it in the future.
    levelDependentPieces: LevelDependentPieces[] = [];

    specialPieces: PieceSpawnRules[] = [];

    blobPrefab: Object3D | null = null;

    private _spawnOrder: LevelDependentPieces[] = null;
    private _piecesToSpawn: LevelDependentPieces[] = null;
    private _pieceCount = 0;
    private _allowedPieces: string[];

    start(): void {
        this.blobPrefabsObject.getComponent(BlobPrefabs)!.onPrefabsLoaded.add((o) => {
            this.blobPrefab = o;
        });
        this.specialPieces = GameModePieceSpawnRules.instance.getRules(this.gameMode);
    }

    onActivate(): void {
        Puzzle.instance.registerNewGameCallback(this._newGame);
    }

    onDeactivate(): void {
        Puzzle.instance.unregisterNewGameCallback(this._newGame);
    }

    spawnNewLine(puzzle: Puzzle) {
        // code from the original LineSpawnerNormal.cs
        for (let x = 0; x < this.levelDependentPieces.length; x++) {
            if (
                this.levelDependentPieces[x] != null &&
                this.levelDependentPieces[x].minimumLevelToSpawn == puzzle.level
            ) {
                this._allowedPieces.push(this.levelDependentPieces[x].piece);
                this.levelDependentPieces[x] = null;
            }
        }

        const columnCount = puzzle.lanes.length;

        if (this._spawnOrder == null) {
            this._spawnOrder = new Array(columnCount);
            this._piecesToSpawn = new Array(columnCount);
        }

        let top = 0;

        let startIndex = RNG.getUniformInt(0, this.specialPieces.length);
        for (let x = 0; x < this.specialPieces.length; x++) {
            const index = (startIndex + x) % this.specialPieces.length;
            this.specialPieces[index].reset(puzzle);
            const spawnCount = this.specialPieces[index].canSpawnCount();
            if (spawnCount > 0 && top < columnCount) {
                for (let y = 0; y < spawnCount && top < columnCount; y++) {
                    const obj = this.specialPieces[index].spawn(puzzle);
                    if (obj) {
                        this._piecesToSpawn[top] = obj;
                        top++;
                    }
                }
            }
        }

        this._weightedDistribution(puzzle);

        // code from the original LineSpawner.cs
        puzzle.addRow();

        // fill the bottom row with pieces
        for (let x = 0; x < columnCount; x++) {
            const pieceName = RNG.getItem(this._allowedPieces);
            //const piece = this.blobPrefab.findByName(pieceName)[0];

            // TODO: add some checks to see if the piece is valid
            // const boardObj = piece.getComponent(BoardObject)!;

            // Note: The original has a lot of optimization being done here,
            // for example to do some dynamic batching. This is omitted for now.

            // TK: Cloning is one of the worst performant actions.
            //const newPiece = WLUtils.clone(piece);
            const newPiece = BlobPrefabs.instance.spawn(pieceName, null, false);
            const boardObject = newPiece.getComponent(BoardObject);
            puzzle.addObject(boardObject, 0, x);
            boardObject.place();

            // shift it down so that the block will appear to move up into its actual row.
            newPiece.getPositionLocal(tempVec3);
            tempVec3[1] -= PuzzleConstants.rowSize;
            newPiece.setPositionLocal(tempVec3);

            const specialPrefab = this._tryToSpawnSpecial(puzzle);
            if (specialPrefab) {
                const payload = BlobPrefabs.instance.spawn(specialPrefab.piece);
                boardObject.loadObject(payload);
            }
        }

        this.lineFinished(puzzle);
    }

    lineFinished(puzzle: Puzzle) {
        for (let x = 0; x < this._spawnOrder.length; x++) {
            this._spawnOrder[x] = null;
            this._piecesToSpawn[x] = null;
        }

        this._pieceCount = 0;
    }

    private _weightedDistribution(puzzle: Puzzle) {
        for (let x = 0; x < this._piecesToSpawn.length; x++) {
            const obj = this._piecesToSpawn[x];
            if (obj) {
                let highestWeight = 0.0;
                let highestIndex = -1;

                // start with a random index
                let startIndex = RNG.getUniformInt(0, this._spawnOrder.length);
                for (let y = 0; y < this._spawnOrder.length; y++) {
                    const index = (startIndex + y) % this._spawnOrder.length;
                    if (this._spawnOrder[index] == null) {
                        let weight = PuzzleUtils.computeSpawnQualityNewRow(
                            this._spawnOrder,
                            index,
                            obj.tag,
                            puzzle
                        );
                        if (weight > highestWeight || highestIndex < 0) {
                            highestIndex = index;
                            highestWeight = weight;
                        }
                        if (weight == 1.0) {
                            break; // this spot is perfect
                        }
                    }
                }

                if (highestIndex >= 0) {
                    this._spawnOrder[highestIndex] = obj;
                }
            } else {
                break;
            }
        }
    }

    private _tryToSpawnSpecial(puzzle: Puzzle): LevelDependentPieces {
        const prefab = this._spawnOrder[this._pieceCount];
        this._pieceCount++;
        return prefab;
    }

    private _newGame = (puzzle: Puzzle) => {
        // set the allowed colors
        this._allowedPieces = [];

        for (const [pieceName, color] of this.pieces) {
            if (LevelSelect.instance.colorAcceptable(color)) {
                this._allowedPieces.push(pieceName);
            }
        }
    };
}

/**
 * This component is used to add a piece to the LineSpawner. WLE does not support
 * arrays of objects (yet) so this is a workaround to add pieces to the array in LineSpawner.
 *
 * - The pieces need to be on the same object as the line-spawner.
 * - The pieces inject themselves into the LineSpawner's pieces array.
 */
export class LineSpawnerPiece extends Component {
    static TypeName = 'line-spawner-piece';

    /**
     * The name of the prefab to spawn. The prefabs are defined in th Pieces.wlp file.
     */
    @property.string('Piece Orange')
    pieceName = 'Piece Orange';

    @property.enum(Object.keys(BoardColor).filter((e) => isNaN(Number(e))))
    color: BoardColor = BoardColor.Orange;

    start(): void {
        const lineSpawner = this.object.getComponent(LineSpawnerNormal);
        if (!lineSpawner) {
            console.error(
                'LineSpawnerPiece needs to be on the same object as the LineSpawner'
            );
            return;
        }
        lineSpawner.pieces.set(this.pieceName, this.color);
    }
}
